import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, setUpgradeModalCallback } from './context/AuthContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import UpgradeModal from './components/UpgradeModal';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CalendarsPage from './pages/CalendarsPage';
import CalendarViewPage from './pages/CalendarViewPage';
import CalendarSettingsPage from './pages/CalendarSettingsPage';
import PostsPage from './pages/PostsPage';
import PostComposer from './pages/PostComposer';
import PostDetailPage from './pages/PostDetailPage';
import ContentIdeasPage from './pages/ContentIdeasPage';
import MediaLibraryPage from './pages/MediaLibraryPage';
import AnalyticsPage from './pages/AnalyticsPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DemoVideoPage from './pages/DemoVideoPage';
import TemplatesPage from './pages/TemplatesPage';
import CompetitorsPage from './pages/CompetitorsPage';
import ReportsPage from './pages/ReportsPage';
import BrandGuidelinesPage from './pages/BrandGuidelinesPage';
import WebhooksPage from './pages/WebhooksPage';
import SocialListeningPage from './pages/SocialListeningPage';
import WorkflowsPage from './pages/WorkflowsPage';
import ABTestingPage from './pages/ABTestingPage';
import FeatureGuidePage from './pages/FeatureGuidePage';

// Marketing/Static Pages
import ProductPage from './pages/ProductPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import IntegrationsPage from './pages/IntegrationsPage';
import APIPage from './pages/APIPage';
import ResourcesPage from './pages/ResourcesPage';
import BlogPage from './pages/BlogPage';
import HelpCenterPage from './pages/HelpCenterPage';
import CommunityPage from './pages/CommunityPage';
import WebinarsPage from './pages/WebinarsPage';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';

import './App.css';

// ScrollToTop component - resets scroll position on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Remove floating badge
const RemoveBadge = () => {
  React.useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    };
    remove();
    const interval = setInterval(remove, 200);
    const observer = new MutationObserver(remove);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); observer.disconnect(); };
  }, []);
  return null;
};

// Router wrapper to handle auth callback
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Calendars */}
      <Route path="/calendars" element={<ProtectedRoute><CalendarsPage /></ProtectedRoute>} />
      <Route path="/calendars/new" element={<ProtectedRoute><CalendarsPage /></ProtectedRoute>} />
      <Route path="/calendars/:calendarId" element={<ProtectedRoute><CalendarViewPage /></ProtectedRoute>} />
      <Route path="/calendars/:calendarId/settings" element={<ProtectedRoute><CalendarSettingsPage /></ProtectedRoute>} />
      
      {/* Posts */}
      <Route path="/posts" element={<ProtectedRoute><PostsPage /></ProtectedRoute>} />
      <Route path="/posts/new" element={<ProtectedRoute><PostComposer /></ProtectedRoute>} />
      <Route path="/posts/:postId" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
      <Route path="/posts/:postId/edit" element={<ProtectedRoute><PostComposer /></ProtectedRoute>} />
      
      {/* Content Ideas */}
      <Route path="/ideas" element={<ProtectedRoute><ContentIdeasPage /></ProtectedRoute>} />
      
      {/* Media Library */}
      <Route path="/media" element={<ProtectedRoute><MediaLibraryPage /></ProtectedRoute>} />
      
      {/* Analytics */}
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* Templates */}
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      
      {/* Brand Guidelines */}
      <Route path="/brand-guidelines" element={<ProtectedRoute><BrandGuidelinesPage /></ProtectedRoute>} />
      
      {/* Competitors */}
      <Route path="/competitors" element={<ProtectedRoute><CompetitorsPage /></ProtectedRoute>} />
      
      {/* Reports */}
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      
      {/* Webhooks & Custom Channels */}
      <Route path="/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
      
      {/* Social Listening */}
      <Route path="/listening" element={<ProtectedRoute><SocialListeningPage /></ProtectedRoute>} />
      
      {/* Workflows */}
      <Route path="/workflows" element={<ProtectedRoute><WorkflowsPage /></ProtectedRoute>} />
      
      {/* A/B Testing */}
      <Route path="/ab-testing" element={<ProtectedRoute><ABTestingPage /></ProtectedRoute>} />
      
      {/* Feature Guide */}
      <Route path="/guide" element={<ProtectedRoute><FeatureGuidePage /></ProtectedRoute>} />
      
      {/* Team */}
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      
      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      
      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/billing" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      {/* Marketing/Static Pages (Public) */}
      <Route path="/product" element={<ProductPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/developers" element={<APIPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/webinars" element={<WebinarsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />

      {/* Legal Pages (Public) */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/demo" element={<DemoVideoPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// UpgradeModal wrapper that connects to the global callback
const UpgradeModalWrapper = () => {
  const [upgradeInfo, setUpgradeInfo] = useState(null);

  useEffect(() => {
    setUpgradeModalCallback((info) => setUpgradeInfo(info));
    return () => setUpgradeModalCallback(null);
  }, []);

  return (
    <UpgradeModal
      open={!!upgradeInfo}
      onClose={() => setUpgradeInfo(null)}
      feature={upgradeInfo?.feature}
      requiredPlan={upgradeInfo?.requiredPlan}
    />
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RemoveBadge />
        <UpgradeModalWrapper />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
